import React, { useState } from 'react';
import { Container, Row, Col, Badge, Button } from 'react-bootstrap';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FaGithub, 
  FaCode, 
  FaLock, 
  FaRegClock, 
  FaJs, 
  FaPhp, 
  FaJava,
  FaCodeBranch,
  FaExternalLinkAlt,
  FaSearch
} from 'react-icons/fa';

const Projects = ({ currentTheme }) => {
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const projects = [
    {
      title: "Apbc",
      description: "Apbc Africa web APP for streamlined business operations.",
      type: "JavaScript",
      isPrivate: true,
      isForked: true,
      forkedFrom: "apbcafricait/Apbc",
      lastUpdated: "3 days ago",
      category: "web-app",
      repoLink: "https://github.com/OMONDISOFTWAREENGINEER/Apbc",
      demoLink: "https://apbcafrica.com/"
    },
    {
      title: "Hospital-Management-System",
      description: "Comprehensive hospital management solution for efficient healthcare administration.",
      type: "PHP",
      isPrivate: false,
      lastUpdated: "Nov 25, 2024",
      category: "healthcare",
      repoLink: "https://github.com/OMONDISOFTWAREENGINEER/Hospital-Management-System",
      demoLink: "https://hospital-management-demo.netlify.app"
    },
    {
      title: "Nyeri-Telemedicine",
      description: "Telemedicine platform connecting patients with healthcare providers in Nyeri.",
      type: "JavaScript",
      isPrivate: false,
      lastUpdated: "Nov 20, 2024",
      category: "healthcare",
      repoLink: "https://github.com/OMONDISOFTWAREENGINEER/Nyeri-Telemedicine",
      demoLink: "https://nyeri-telemedicine-demo.netlify.app"
    },
    {
      title: "SPARESGUY",
      description: "Auto parts management system for efficient inventory tracking and sales.",
      type: "JavaScript",
      isPrivate: false,
      isForked: true,
      forkedFrom: "apbcafricait/SPARESGUYv1",
      lastUpdated: "Nov 20, 2024",
      category: "e-commerce",
      repoLink: "https://github.com/OMONDISOFTWAREENGINEER/SPARESGUYv1",
      demoLink: "https://sparesguyv1-demo.netlify.app"
    },
    {
      title: "Trademark",
      description: "Trademark management system for intellectual property protection.",
      type: "JavaScript",
      isPrivate: true,
      isForked: true,
      forkedFrom: "apbcafricait/trademark",
      lastUpdated: "Oct 18, 2024",
      category: "management",
      repoLink: "https://github.com/OMONDISOFTWAREENGINEER/trademark",
      demoLink: "https://trademark-demo.netlify.app"
    },
    {
      title: "HR",
      description: "Human Resources management system for streamlined HR operations.",
      type: "PHP",
      isPrivate: true,
      isForked: true,
      forkedFrom: "apbcafricait/HR",
      lastUpdated: "Oct 4, 2024",
      category: "management",
      repoLink: "https://github.com/OMONDISOFTWAREENGINEER/HR",
      demoLink: "https://hr-system-demo.netlify.app"
    },
    {
      title: "JM-KARIUKI",
      description: "J.M. Kariuki Memorial Hospital Telemedicine Web Application for remote healthcare services.",
      type: "JavaScript",
      isPrivate: false,
      isForked: true,
      forkedFrom: "Ismael-Njihia/JM-KARIUKI",
      lastUpdated: "Sep 3, 2024",
      category: "healthcare",
      repoLink: "https://github.com/OMONDISOFTWAREENGINEER/JM-KARIUKI",
      demoLink: "https://jm-kariuki-telemedicine-demo.netlify.app"
    },
    {
      title: "Jibuks",
      description: "Java-based application for book management and inventory tracking.",
      type: "Java",
      isPrivate: true,
      isForked: true,
      forkedFrom: "apbcafricait/jibuks",
      lastUpdated: "Aug 19, 2024",
      category: "management",
      repoLink: "https://github.com/OMONDISOFTWAREENGINEER/jibuks",
      demoLink: "https://jibuks-demo.netlify.app"
    }
  ];

  const categories = [
    { name: 'all', label: 'All Projects' },
    { name: 'healthcare', label: 'Healthcare' },
    { name: 'web-app', label: 'Web Apps' },
    { name: 'e-commerce', label: 'E-Commerce' },
    { name: 'management', label: 'Management' }
  ];

  const getTypeIcon = (type) => {
    switch(type) {
      case 'JavaScript': return <FaJs style={{ color: '#F7DF1E' }} />;
      case 'PHP': return <FaPhp style={{ color: '#777BB4' }} />;
      case 'Java': return <FaJava style={{ color: '#007396' }} />;
      default: return <FaCode />;
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 }
    },
    exit: {
      opacity: 0,
      y: -20,
      transition: { duration: 0.3 }
    }
  };

  const filteredProjects = projects.filter(project => 
    (filter === 'all' || project.category === filter) &&
    project.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Container fluid className="py-5 position-relative overflow-hidden" id="projects" style={{ backgroundColor: currentTheme.background }}>
      <div className="position-absolute w-100 h-100 top-0 start-0" style={{ zIndex: -1 }}>
        <motion.div
          animate={{
            background: [
              `linear-gradient(45deg, ${currentTheme.primary} 0%, ${currentTheme.secondary} 100%)`,
              `linear-gradient(45deg, ${currentTheme.secondary} 0%, ${currentTheme.primary} 100%)`
            ],
          }}
          transition={{ duration: 3, repeat: Infinity, repeatType: 'reverse' }}
          className="w-100 h-100"
        />
      </div>

      <Container className="position-relative">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={containerVariants}
        >
          <Row className="justify-content-center mb-5">
            <Col lg={8} className="text-center">
              <motion.h2 
                className="display-4 fw-bold mb-4"
                variants={itemVariants}
                style={{ color: currentTheme.primary }}
              >
                PROJECTS
              </motion.h2>
              <motion.div 
                className="w-25 mx-auto border-bottom border-3 mb-4"
                variants={itemVariants}
                style={{ borderColor: currentTheme.primary }}
              />
            </Col>
          </Row>

          <Row className="mb-5">
            <Col lg={8} className="mx-auto">
              <div className="input-group mb-3">
                <span className="input-group-text text-white" style={{ backgroundColor: currentTheme.primary }}>
                  <FaSearch />
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search projects..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{ borderColor: currentTheme.primary, color: currentTheme.text, backgroundColor: currentTheme.background }}
                />
              </div>
            </Col>
          </Row>

          <Row className="mb-5">
            <Col className="d-flex justify-content-center flex-wrap gap-2">
              {categories.map((category) => (
                <motion.button
                  key={category.name}
                  onClick={() => setFilter(category.name)}
                  className={`btn rounded-pill px-4 py-2 m-1`}
                  style={{
                    backgroundColor: filter === category.name ? currentTheme.primary : 'transparent',
                    color: filter === category.name ? currentTheme.background : currentTheme.primary,
                    borderColor: currentTheme.primary
                  }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {category.label}
                </motion.button>
              ))}
            </Col>
          </Row>

          <Row className="g-4">
            <AnimatePresence mode="wait">
              {filteredProjects.map((project, index) => (
                <Col lg={6} key={project.title}>
                  <motion.div
                    variants={itemVariants}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                    className="card h-100 border-0 shadow-sm position-relative overflow-hidden"
                    whileHover={{ y: -10 }}
                    style={{ backgroundColor: currentTheme.background === '#121212' ? '#222222' : '#FFFFFF', color: currentTheme.text }}
                  >
                    <div className="card-body p-4">
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <div className="d-flex align-items-center">
                          <div className="me-3">
                            {project.isPrivate ? 
                              <FaLock style={{ color: currentTheme.text }} /> : 
                              <FaGithub style={{ color: currentTheme.text }} />
                            }
                          </div>
                          <h3 className="h5 mb-0" style={{ color: currentTheme.text }}>{project.title}</h3>
                        </div>
                        <Badge bg={project.isPrivate ? 'secondary' : 'success'} style={{ color: currentTheme.background, backgroundColor: project.isPrivate ? currentTheme.secondary : currentTheme.primary }}>
                          {project.isPrivate ? 'Private' : 'Public'}
                        </Badge>
                      </div>
                      <p style={{ color: currentTheme.text }}>{project.description}</p>

                      <div className="d-flex align-items-center mb-3">
                        {getTypeIcon(project.type)}
                        <span className="ms-2" style={{ color: currentTheme.text }}>{project.type}</span>
                      </div>

                      {project.isForked && (
                        <div className="d-flex align-items-center mb-3" style={{ color: currentTheme.text }}>
                          <FaCodeBranch className="me-2" />
                          <small>Forked from {project.forkedFrom}</small>
                        </div>
                      )}

                      <div className="d-flex align-items-center mb-3" style={{ color: currentTheme.text }}>
                        <FaRegClock className="me-2" />
                        <small>Updated {project.lastUpdated}</small>
                      </div>

                      <div className="d-flex justify-content-between">
                        <Button variant="outline-primary" size="sm" href={project.repoLink} target="_blank" style={{ borderColor: currentTheme.primary, color: currentTheme.primary }}>
                          <FaGithub className="me-2" />
                          View Code
                        </Button>
                        <Button variant="outline-success" size="sm" href={project.demoLink} target="_blank" style={{ borderColor: currentTheme.secondary, color: currentTheme.secondary }}>
                          <FaExternalLinkAlt className="me-2" />
                          Live Demo
                        </Button>
                      </div>
                    </div>
                  </motion.div>
                </Col>
              ))}
            </AnimatePresence>
          </Row>
        </motion.div>

        <motion.div
          animate={{
            rotate: 360,
            transition: { duration: 20, repeat: Infinity, ease: "linear" }
          }}
          className="position-absolute"
          style={{ top: '10%', right: '5%', opacity: 0.1 }}
        >
          <FaCode style={{ fontSize: '4rem', color: currentTheme.primary }} />
        </motion.div>
      </Container>
    </Container>
  );
};

export default Projects;